import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, extractFormError } from "@/lib/api";
import { track } from "@/lib/analytics";

export default function LeadForm({ source = "contact_form" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    interest: "Growth Engine",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", { ...form, source });
      track("Lead Submitted", { interest: form.interest, source });
      setDone(true);
      toast.success("Thanks. I'll be in touch within 24 hours.");
      setForm({
        name: "",
        email: "",
        company: "",
        interest: "Growth Engine",
        message: "",
      });
    } catch (err) {
      toast.error(extractFormError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="lead-form-success"
        className="border border-gold/30 p-10 lg:p-14 bg-white"
      >
        <p className="overline">Received</p>
        <h3 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
          Welcome, friend.
          <br />
          <span className="italic text-aurora">I&apos;ll be in touch.</span>
        </h3>
        <p className="text-neutral-500 mt-5 leading-relaxed">
          Your submission landed safely. I personally read every enquiry
          and will reply within one business day.
        </p>
        <button
          onClick={() => setDone(false)}
          data-testid="lead-form-reset"
          className="btn-ghost mt-8"
        >
          Send another <ArrowUpRight size={14} />
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="lead-form"
      className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10"
    >
      <div className="md:col-span-1">
        <label className="overline block mb-3" htmlFor="lf-name">
          Your name
        </label>
        <input
          id="lf-name"
          data-testid="lead-form-name"
          required
          value={form.name}
          onChange={update("name")}
          className="editorial-input"
          placeholder="Jane Doe"
        />
      </div>
      <div className="md:col-span-1">
        <label className="overline block mb-3" htmlFor="lf-email">
          Email
        </label>
        <input
          id="lf-email"
          data-testid="lead-form-email"
          required
          type="email"
          value={form.email}
          onChange={update("email")}
          className="editorial-input"
          placeholder="jane@company.com"
        />
      </div>
      <div className="md:col-span-1">
        <label className="overline block mb-3" htmlFor="lf-company">
          Company
        </label>
        <input
          id="lf-company"
          data-testid="lead-form-company"
          value={form.company}
          onChange={update("company")}
          className="editorial-input"
          placeholder="Optional"
        />
      </div>
      <div className="md:col-span-1">
        <label className="overline block mb-3" htmlFor="lf-interest">
          I&apos;m interested in
        </label>
        <select
          id="lf-interest"
          data-testid="lead-form-interest"
          value={form.interest}
          onChange={update("interest")}
          className="editorial-input bg-white"
        >
          <option className="bg-white">Studio Builder</option>
          <option className="bg-white">Growth Engine</option>
          <option className="bg-white">Brand & Voice</option>
          <option className="bg-white">Success Insights</option>
          <option className="bg-white">Just saying hi</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="overline block mb-3" htmlFor="lf-msg">
          Tell me a bit about your project
        </label>
        <textarea
          id="lf-msg"
          data-testid="lead-form-message"
          rows={4}
          value={form.message}
          onChange={update("message")}
          className="editorial-input resize-none"
          placeholder="The shorter the better. Goals, timeline, any URLs..."
        />
      </div>
      <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mt-2">
        <p className="text-xs text-neutral-400 max-w-md">
          By submitting, you agree to receive a personal reply. I never share
          your details and I never spam.
        </p>
        <button
          type="submit"
          disabled={submitting}
          className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="lead-form-submit"
        >
          {submitting ? "Sending..." : "Send enquiry"} <ArrowUpRight size={16} />
        </button>
      </div>
    </form>
  );
}
